import { useLayoutEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useNavigationQuery } from '../../api/portalGateway/navigation';
import {
  fetchPageFilterData,
  PageFilterQueryKey,
} from '../../api/portalGateway/pagefilterData';
import { useStore } from '../../store';
import { useSelections } from '../../store/hooks/useSelections';
import { FilterConfigDto, FilterData } from '../../store/states/filterTypes';
import { prependRoleId } from '../../utils/prependRoleId';

import {
  EMPTY_FILTER_CONFIGS,
  useSanitizeSelections,
  validateFiltersFromResponse,
} from './filterQueryInternals';

export function useGlobalFilterQuery(
  filterConfigs: FilterConfigDto[] | undefined,
  rootPageId: string | undefined
) {
  const entityId = rootPageId ?? '';
  const globalSelections = useSelections(entityId);
  const isInitialized = useStore(
    (state) => state.filterInitialized[entityId] ?? false
  );
  const filterQueryLoaded = useStore(
    (state) => state.filterQueryLoaded[entityId] ?? false
  );

  const hasFilters = (filterConfigs ?? []).length > 0 && rootPageId != null;

  const query = useQuery({
    queryKey: prependRoleId<PageFilterQueryKey>([
      'pageFilters',
      { pageId: entityId, filterKey: entityId },
      globalSelections,
    ]),
    queryFn: fetchPageFilterData,
    enabled: hasFilters && isInitialized,
  });

  useSanitizeSelections(
    query,
    entityId,
    filterConfigs ?? EMPTY_FILTER_CONFIGS,
    globalSelections,
    (sanitized) =>
      prependRoleId<PageFilterQueryKey>([
        'pageFilters',
        { pageId: entityId, filterKey: entityId },
        sanitized,
      ])
  );

  const { validatedData, validationError } = useMemo(() => {
    if (
      query.data == null ||
      filterConfigs == null ||
      filterConfigs.length === 0
    ) {
      return { validatedData: undefined, validationError: undefined };
    }
    try {
      return {
        validatedData: validateFiltersFromResponse(filterConfigs, query.data),
        validationError: undefined,
      };
    } catch (error) {
      return { validatedData: undefined, validationError: error };
    }
  }, [query.data, filterConfigs]);

  if (validationError) {
    throw validationError;
  }

  // When called with real configs (authoritative): derive from query result.
  // When called without configs (status-check): defer to the store flag so
  // callers block until the authoritative query has completed.
  const isSuccess =
    filterConfigs === undefined
      ? filterQueryLoaded
      : !hasFilters || query.isSuccess;

  useLayoutEffect(() => {
    if (filterConfigs === undefined) {
      return;
    }
    const state = useStore.getState();
    const shouldBeLoaded = !hasFilters || query.isSuccess;
    if (shouldBeLoaded && !state.filterQueryLoaded[entityId]) {
      state.setFilterQueryLoaded(entityId);
    } else if (!shouldBeLoaded && state.filterQueryLoaded[entityId]) {
      state.resetFilterQueryLoaded(entityId);
    }
  }, [filterConfigs, hasFilters, query.isSuccess, entityId, filterQueryLoaded]);

  return {
    data: query.data,
    error: query.error,
    isFetching: query.isFetching,
    isSuccess,
    validatedData,
  };
}

// ---------------------------------------------------------------------------
// Lightweight hook: checks whether the global filter query has finished
// without instantiating a full useGlobalFilterQuery observer.
// ---------------------------------------------------------------------------

export function useGlobalFilterLoaded(): boolean {
  const { data: navData } = useNavigationQuery();
  const rootPageId = navData?.rootPageId;
  const hasGlobalFilters =
    navData != null &&
    (navData.globalFilters?.length ?? 0) > 0 &&
    rootPageId != null;
  return useStore(
    (state) =>
      !hasGlobalFilters || (state.filterQueryLoaded[rootPageId ?? ''] ?? false)
  );
}

// ---------------------------------------------------------------------------
// Read-only global filter validated data: subscribes to the same React Query
// cache as useGlobalFilterQuery but does NOT run useSanitizeSelections or
// manage filterQueryLoaded. Use when you only need validatedData without
// side effects (e.g. computing effective selections).
// ---------------------------------------------------------------------------

export function useGlobalFilterValidatedData(
  filterConfigs: FilterConfigDto[] | undefined,
  rootPageId = ''
): Record<string, FilterData> | undefined {
  const globalSelections = useSelections(rootPageId);

  const { data } = useQuery({
    queryKey: prependRoleId<PageFilterQueryKey>([
      'pageFilters',
      { pageId: rootPageId, filterKey: rootPageId },
      globalSelections,
    ]),
    queryFn: fetchPageFilterData,
    enabled: false,
  });

  return useMemo(() => {
    if (data == null || filterConfigs == null || filterConfigs.length === 0) {
      return undefined;
    }
    try {
      return validateFiltersFromResponse(filterConfigs, data);
    } catch {
      return undefined;
    }
  }, [data, filterConfigs]);
}
